// Mobile drawer (burger + accordion navigation) with focus trap + swipe-to-close.
import { createFocusTrap } from "./focus-trap.js";

export function setupDrawer(navElement, desktopQuery) {
  const navBurger = navElement.querySelector(".mega-nav__burger");
  const drawer = document.getElementById("mobile-drawer");
  const drawerClose = drawer?.querySelector(".drawer__close");
  const accordionToggles =
    drawer?.querySelectorAll(".drawer__accordion-toggle") || [];

  const focusTrap = drawer ? createFocusTrap(drawer) : null;

  // Swipe-to-close state
  let touchStartX = 0;
  let touchCurrentX = 0;
  let isSwiping = false;
  const SWIPE_THRESHOLD = 80;

  const closeDrawer = () => {
    if (!drawer || !navBurger) return;
    drawer.classList.remove("drawer--open");
    drawer.setAttribute("aria-hidden", "true");
    navBurger.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
    drawer.style.transform = "";
    focusTrap?.deactivate();
  };

  const openDrawer = () => {
    if (!drawer || !navBurger) return;
    drawer.classList.add("drawer--open");
    drawer.setAttribute("aria-hidden", "false");
    navBurger.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
    focusTrap?.activate();
  };

  // Mobile drawer toggle
  navBurger?.addEventListener("click", () => {
    if (drawer?.classList.contains("drawer--open")) {
      closeDrawer();
    } else {
      openDrawer();
    }
  });

  drawerClose?.addEventListener("click", closeDrawer);

  // Accordion toggles for mobile sections
  accordionToggles.forEach((toggle) => {
    toggle.addEventListener("click", () => {
      const id = toggle.dataset.mobileTarget;
      if (!drawer) return;
      const targetPanel = drawer.querySelector(`#mobile-${id}`);
      const isOpen = targetPanel && !targetPanel.hasAttribute("hidden");

      drawer.querySelectorAll(".drawer__accordion-panel").forEach((panel) => {
        panel.setAttribute("hidden", "");
      });
      drawer.querySelectorAll(".drawer__accordion-toggle").forEach((btn) => {
        btn.setAttribute("aria-expanded", "false");
      });

      if (targetPanel && !isOpen) {
        targetPanel.removeAttribute("hidden");
        toggle.setAttribute("aria-expanded", "true");
      }
    });
  });

  // Swipe-to-close gesture (right swipe)
  if (drawer) {
    drawer.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.touches[0].clientX;
        touchCurrentX = touchStartX;
        isSwiping = false;
      },
      { passive: true }
    );

    drawer.addEventListener(
      "touchmove",
      (e) => {
        touchCurrentX = e.touches[0].clientX;
        const dx = touchCurrentX - touchStartX;
        // Only track rightward swipes (positive dx)
        if (dx > 20) {
          isSwiping = true;
          // Rubber-band effect: translate the drawer rightward
          const clamped = Math.min(dx, 200);
          drawer.style.transform = `translateX(${clamped}px)`;
          drawer.style.transition = "none";
        }
      },
      { passive: true }
    );

    drawer.addEventListener("touchend", () => {
      const dx = touchCurrentX - touchStartX;
      drawer.style.transition = "";
      if (isSwiping && dx >= SWIPE_THRESHOLD) {
        closeDrawer();
      } else {
        // Snap back
        drawer.style.transform = "";
      }
      isSwiping = false;
    });
  }

  // If resizing back to desktop, force-close the drawer
  window.addEventListener("resize", () => {
    if (desktopQuery.matches) closeDrawer();
  });

  return { closeDrawer };
}
